import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable single-file uploader.
 * value = "" or URL string; calls onChange(url) on successful upload.
 * accept = HTML accept string; defaults to PDFs + images.
 */
export default function FileUploadField({ label, value, onChange, accept = "image/png,image/jpeg,application/pdf", testid = "file-upload" }) {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/org/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data.url);
      toast.success(`${label || "File"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-1">
      {label ? <label className="text-xs font-medium">{label}</label> : null}
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-border p-2 bg-muted/40">
          <FileText className="w-4 h-4 text-emerald-700" />
          <a href={value} target="_blank" rel="noreferrer" className="text-xs underline truncate flex-1">{value.split("/").pop()}</a>
          <button onClick={() => onChange?.("")} className="text-muted-foreground hover:text-destructive" type="button"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <div className="relative">
          <Input type="file" accept={accept} onChange={handle} disabled={busy} data-testid={testid} className="text-xs file:mr-2" />
          {busy ? <div className="absolute inset-0 grid place-items-center bg-muted/50 text-xs">Uploading…</div> : null}
        </div>
      )}
    </div>
  );
}
